/* eslint-disable linebreak-style */
import {
  setProduse,
  getProduseX,
  setMateriale,
  getMaterialeX,
  setPreturiTabelare,
  getPreturiTabelareX,
  setOptionale,
  getOptionaleX,
  setCulori,
  getCuloriX,
  setIndicatori,
  getIndicatoriX,
  setInformari,
  getInformariX,
  setFirma,
  getFirmaX
} from '../strapi/strapi.utils';
import {isFeatureEnabled} from '../config/featureFlags';
import {openLookupDb} from './lookupDb';

export const LOOKUP_CACHE_KEYS = Object.freeze({
  PRODUSE: 'produse',
  MATERIALE: 'materiale',
  PRETURI_TABELARE: 'preturitabelare',
  OPTIONALE: 'optionale',
  CULORI: 'culori',
  INDICATORI: 'indicatori',
  INFORMARI: 'informari',
  FIRMA: 'firma'
});

const localReaders = {
  [LOOKUP_CACHE_KEYS.PRODUSE]: getProduseX,
  [LOOKUP_CACHE_KEYS.MATERIALE]: getMaterialeX,
  [LOOKUP_CACHE_KEYS.PRETURI_TABELARE]: getPreturiTabelareX,
  [LOOKUP_CACHE_KEYS.OPTIONALE]: getOptionaleX,
  [LOOKUP_CACHE_KEYS.CULORI]: getCuloriX,
  [LOOKUP_CACHE_KEYS.INDICATORI]: getIndicatoriX,
  [LOOKUP_CACHE_KEYS.INFORMARI]: getInformariX,
  [LOOKUP_CACHE_KEYS.FIRMA]: getFirmaX
};

const localWriters = {
  [LOOKUP_CACHE_KEYS.PRODUSE]: setProduse,
  [LOOKUP_CACHE_KEYS.MATERIALE]: setMateriale,
  [LOOKUP_CACHE_KEYS.PRETURI_TABELARE]: setPreturiTabelare,
  [LOOKUP_CACHE_KEYS.OPTIONALE]: setOptionale,
  [LOOKUP_CACHE_KEYS.CULORI]: setCulori,
  [LOOKUP_CACHE_KEYS.INDICATORI]: setIndicatori,
  [LOOKUP_CACHE_KEYS.INFORMARI]: setInformari,
  [LOOKUP_CACHE_KEYS.FIRMA]: setFirma
};

const logCacheWarning = (message, error)=>{
  // eslint-disable-next-line no-console
  console.warn(`[lookupCacheStorage] ${message}`, error || '');
};

const logFallbackDebug = (cacheKey, reason, details = {})=>{
  // eslint-disable-next-line no-console
  console.warn(`[lookupCacheStorage][fallback] key=${cacheKey} reason=${reason}`, details);
};

const isIndexedDbEnabled = ()=> isFeatureEnabled('indexedDbLookupCache');

const LOOKUP_STORE = 'lookupCache';

const readFromIndexedDb = async(cacheKey)=>{
  const db = await openLookupDb();
  return new Promise((resolve, reject)=>{
    const tx = db.transaction(LOOKUP_STORE, 'readonly');
    const store = tx.objectStore(LOOKUP_STORE);
    const request = store.get(cacheKey);

    request.onsuccess = ()=> {
      const payload = request.result;
      resolve(payload ? payload.value : null);
    };
    request.onerror = ()=> reject(request.error || new Error('Nu am putut citi din IndexedDB'));
  });
};

const writeToIndexedDb = async(cacheKey, value)=>{
  const db = await openLookupDb();
  return new Promise((resolve, reject)=>{
    const tx = db.transaction(LOOKUP_STORE, 'readwrite');
    const store = tx.objectStore(LOOKUP_STORE);
    const request = store.put({
      cacheKey,
      value,
      updatedAt: new Date().toISOString()
    });

    request.onsuccess = ()=> resolve(true);
    request.onerror = ()=> reject(request.error || new Error('Nu am putut scrie in IndexedDB'));
  });
};

const readFromLocalStorage = (cacheKey)=>{
  try {
    const reader = localReaders[cacheKey];
    return reader ? reader() : null;
  } catch (error) {
    logCacheWarning(`Eroare la citirea din localStorage pentru ${cacheKey}`, error);
    return null;
  }
};

const writeToLocalStorage = (cacheKey, value)=>{
  try {
    const writer = localWriters[cacheKey];
    if (writer) {
      writer(value);
    }
  } catch (error) {
    logCacheWarning(`Eroare la scrierea in localStorage pentru ${cacheKey}`, error);
  }
};

export const getLookupCache = async(cacheKey)=>{
  const localValue = readFromLocalStorage(cacheKey);
  if (!isIndexedDbEnabled()) {
    return localValue;
  }

  try {
    const indexedDbValue = await readFromIndexedDb(cacheKey);
    if (indexedDbValue !== null && indexedDbValue !== undefined) {
      writeToLocalStorage(cacheKey, indexedDbValue);
      return indexedDbValue;
    }

    if (localValue !== null && localValue !== undefined) {
      writeToIndexedDb(cacheKey, localValue).catch((error)=>{
        logCacheWarning(`Nu am putut sincroniza in IndexedDB pentru ${cacheKey}`, error);
      });
    }
    return localValue;
  } catch (error) {
    logFallbackDebug(cacheKey, 'indexeddb-read-error', {
      hasLocalValue: localValue !== null && localValue !== undefined,
      message: error?.message || 'unknown'
    });
    logCacheWarning(`Fallback localStorage activ pentru ${cacheKey}`, error);
    return localValue;
  }
};

/**
 * Production-safe dual-write:
 * - localStorage ramane sursa primara in valul initial
 * - IndexedDB este actualizat in paralel, cu fallback silent
 */
export const setLookupCache = async(cacheKey, value)=>{
  writeToLocalStorage(cacheKey, value);
  if (!isIndexedDbEnabled()) {
    return;
  }
  try {
    await writeToIndexedDb(cacheKey, value);
  } catch (error) {
    logCacheWarning(`Nu am putut salva in IndexedDB pentru ${cacheKey}`, error);
  }
};

